"use client";

import * as React from "react";
import { ChevronRight, ExternalLink, Book, Globe, FileText, GraduationCap } from "lucide-react";
import { cn } from "@/lib/utils";

export type SourceType = "website" | "book" | "journal" | "article" | "other";

export interface SourceItem {
  id: string;
  title: string;
  authors?: string[];
  date?: string;
  url?: string;
  type: SourceType;
  publication?: string;
  doi?: string;
  isbn?: string;
  pages?: string;
  volume?: string;
  issue?: string;
  publisher?: string;
  accessDate?: string;
  description?: string | React.ReactNode;
}

export interface SourcesProps {
  items: SourceItem[];
  title?: string;
  showViewAll?: boolean;
  viewAllText?: string;
  onViewAllClick?: () => void;
  className?: string;
  itemClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
}

const getSourceIcon = (type: SourceType) => {
  switch (type) {
    case "website":
      return Globe;
    case "book":
      return Book;
    case "journal":
      return GraduationCap;
    case "article":
      return FileText;
    default:
      return FileText;
  }
};

const formatAuthors = (authors?: string[]) => {
  if (!authors || authors.length === 0) return null;
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]} & ${authors[1]}`;
  return `${authors[0]} et al.`;
};

const Sources = React.forwardRef<HTMLDivElement, SourcesProps>(
  (
    {
      items,
      title = "Sources & References",
      showViewAll = false,
      viewAllText = "View all sources",
      onViewAllClick,
      className,
      itemClassName,
      headerClassName,
      contentClassName,
      ...props
    },
    ref
  ) => {
    const [openItems, setOpenItems] = React.useState<Set<string>>(new Set());

    const toggleItem = (id: string) => {
      setOpenItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    };

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">{title}</h2>
          {showViewAll && (
            <button
              onClick={onViewAllClick}
              className="text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
            >
              {viewAllText}
            </button>
          )}
        </div>

        {/* Source Items */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {items.map((item, index) => {
            const isOpen = openItems.has(item.id);
            const Icon = getSourceIcon(item.type);
            
            return (
              <div
                key={item.id}
                className={cn(
                  "border-b border-gray-200 last:border-b-0",
                  itemClassName
                )}
              >
                {/* Source Header */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-200",
                    headerClassName
                  )}
                >
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 text-gray-500 transition-transform duration-500 shrink-0",
                      isOpen && "rotate-90"
                    )}
                  />
                  <Icon className="h-4 w-4 text-gray-500 shrink-0" />
                  <span className="flex flex-col">
                    <span className="text-black font-medium text-sm">
                      {item.title}
                    </span>
                    {(item.authors || item.date) && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {formatAuthors(item.authors)}
                        {item.authors && item.date && " • "}
                        {item.date}
                      </span>
                    )}
                  </span>
                </button>

                {/* Source Details */}
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-500 ease-in-out",
                    isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                  )}
                >
                  <div className={cn("px-2 py-1.5 text-black leading-tight", contentClassName)}>
                    <div className="space-y-1">
                      {/* Citation Details */}
                      <div className="space-y-0.5 text-xs text-gray-600">
                        {item.authors && item.authors.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Authors: </span>
                            {item.authors.join(", ")}
                          </div>
                        )}
                        
                        {item.publication && (
                          <div>
                            <span className="font-medium text-gray-700">Publication: </span>
                            {item.publication}
                          </div>
                        )}
                        
                        {item.publisher && (
                          <div>
                            <span className="font-medium text-gray-700">Publisher: </span>
                            {item.publisher}
                          </div>
                        )}
                        
                        {item.date && (
                          <div>
                            <span className="font-medium text-gray-700">Date: </span>
                            {item.date}
                          </div>
                        )}
                        
                        {item.volume && (
                          <div>
                            <span className="font-medium text-gray-700">Volume: </span>
                            {item.volume}
                            {item.issue && ` (${item.issue})`}
                          </div>
                        )}
                        
                        {item.pages && (
                          <div>
                            <span className="font-medium text-gray-700">Pages: </span>
                            {item.pages}
                          </div>
                        )}
                        
                        {item.doi && (
                          <div>
                            <span className="font-medium text-gray-700">DOI: </span>
                            <a 
                              href={`https://doi.org/${item.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {item.doi}
                            </a>
                          </div>
                        )}
                        
                        {item.isbn && (
                          <div>
                            <span className="font-medium text-gray-700">ISBN: </span>
                            {item.isbn}
                          </div>
                        )}
                        
                        {item.url && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-gray-700">URL: </span>
                            <a 
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.url.length > 60 ? `${item.url.substring(0, 60)}...` : item.url}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                        
                        {item.accessDate && (
                          <div>
                            <span className="font-medium text-gray-700">Accessed: </span>
                            {item.accessDate}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

Sources.displayName = "Sources";

export { Sources };

